/**
 * Зеркало реестра площадок для фронта.
 *
 * Источник правды — `shared/marketplace-registry/src/index.ts` (бэкенд). Фронт
 * не видит `shared/`, поэтому коды, порядок, профили и функции ниже держим в
 * ДОСЛОВНОМ согласии с бэкенд-файлом: любая правка одного — сразу же и другого.
 * Расхождение — это ровно тот баг, ради которого реестр вообще завели
 * (см. `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §3.1): третья площадка молча
 * уходит в ветку OZON у тернарников `marketplace === 'wb' ? A : B`.
 *
 *   1. Код площадки — только `MarketplaceCode` отсюда, локальных union не заводим.
 *   2. Различие площадок — поле профиля ниже или `switch` с `assertNever`,
 *      но не тернарник «wb или всё остальное».
 *   3. Незнакомое значение — ошибка (`requireMarketplace`), а не молчаливый OZON.
 *
 * UI-поля, которых на бэкенде нет (тон бейджа и т.п.), вынесены отдельной
 * картой в конце файла — они не часть контракта с бэкендом.
 */

import type { BadgeVariant } from '../components/ui/Badge'

/** Площадки, на которые ПИМ выгружает карточки. Порядок = порядок показа в UI. */
export const MARKETPLACE_CODES = ['wb', 'ozon', 'ym'] as const
export type MarketplaceCode = (typeof MARKETPLACE_CODES)[number]

/** Каналы карточек: площадки плюс собственный сайт (у сайта нет транспорта площадки). */
export const CHANNEL_CODES = [...MARKETPLACE_CODES, 'site'] as const
export type ChannelCode = (typeof CHANNEL_CODES)[number]

/** Значение `mp_sellers.marketplace` (исторически длинное имя). */
export type SellerMarketplace = 'wildberries' | 'ozon' | 'yandex_market'

export interface MarketplaceProfile {
  code: MarketplaceCode
  /** Значение в `mp_sellers.marketplace`. */
  sellerMarketplace: SellerMarketplace
  /** Короткая подпись в UI: бейджи, колонки, вкладки. */
  label: string
  /** Полное имя площадки для текстов и уведомлений. */
  fullName: string
  /** Имя сервиса-транспорта для Redis-RPC (`service:request:<имя>`). */
  transportService: 'wildberries-service' | 'ozon-service' | 'yandex-market-service'
  /** Путь страницы карточек площадки во фронте. */
  catalogPath: '/wildberries' | '/ozon' | '/yandex-market'
  /**
   * Сколько карточек помещается в одну склейку на площадке.
   * `null` — склейки для площадки ещё не подключены в ПИМ.
   */
  bundleCapacity: number | null
  /**
   * Подтверждённый лимит длины наименования карточки. `null` — лимит в коде не
   * зашит, действует `seo_rulesets` площадки.
   */
  titleMaxLength: number | null
  /** Максимум фото в галерее; OZON пока сохраняет прежний предел 30. */
  photoLimit: number
  /**
   * Возможности, подключённые в ПИМ. Выключенная возможность — осознанный отказ
   * с понятной причиной, а не падение в ветку другой площадки.
   */
  capabilities: {
    /** Выгрузка карточек через ядро `services/marketplace`. */
    publish: boolean
    /** Цена/остаток при выгрузке (коммерция). */
    commercials: boolean
    /** Склейки как сущность `marketplace_bundles`. */
    bundles: boolean
    /** Канал изменений уже выгруженных карточек. */
    cardChanges: boolean
    /** Импорт визуалов с площадки на сайт. */
    visualImport: boolean
  }
}

export const MARKETPLACES: Readonly<Record<MarketplaceCode, MarketplaceProfile>> = {
  wb: {
    code: 'wb',
    sellerMarketplace: 'wildberries',
    label: 'ВБ',
    fullName: 'Wildberries',
    transportService: 'wildberries-service',
    catalogPath: '/wildberries',
    bundleCapacity: 30,
    titleMaxLength: 60,
    photoLimit: 30,
    capabilities: { publish: true, commercials: true, bundles: true, cardChanges: true, visualImport: true },
  },
  ozon: {
    code: 'ozon',
    sellerMarketplace: 'ozon',
    label: 'ОЗОН',
    fullName: 'OZON',
    transportService: 'ozon-service',
    catalogPath: '/ozon',
    // Зеркало shared/marketplace-registry: решение владельца 04.11.2026 —
    // не больше 40 карточек в склейке ОЗОН (площадка допускает 100).
    bundleCapacity: 40,
    titleMaxLength: null,
    photoLimit: 30,
    capabilities: { publish: true, commercials: true, bundles: true, cardChanges: true, visualImport: true },
  },
  ym: {
    code: 'ym',
    sellerMarketplace: 'yandex_market',
    label: 'Я.Маркет',
    fullName: 'Яндекс Маркет',
    transportService: 'yandex-market-service',
    catalogPath: '/yandex-market',
    // Лимит группы вариантов в Partner API не опубликован. Живой кабинет
    // 13.09.2026: 4 281 группа, максимум ровно 30 и много групп на пределе,
    // больше 30 нет ни одной — принимаем 30 как потолок площадки.
    bundleCapacity: 30,
    // offer-mappings/update: name до 256 символов. Жёстко не зашиваем, пока
    // лимит не подтверждён ответом площадки; правило живёт в seo_rulesets.
    titleMaxLength: null,
    photoLimit: 30,
    // Возможности включаются по мере этапов SPEC_2026-09-13_YANDEX_MARKET.
    // Этап 2: выгрузка и цена включены; запись на площадку по-прежнему закрыта
    // предохранителями MARKETPLACE_PUBLISH_MODE, live_exchange и YM_WRITE_ENABLED.
    // Этап 3: склейки и канал изменений. Импорт визуалов остаётся выключенным
    // сознательно: API не отдаёт копии картинок Маркета, а исходные ссылки
    // карточек в основном мертвы (404) — импортировать нечего.
    capabilities: { publish: true, commercials: true, bundles: true, cardChanges: true, visualImport: false },
  },
}

const ALIASES: Readonly<Record<string, MarketplaceCode>> = {
  wb: 'wb',
  wildberries: 'wb',
  ozon: 'ozon',
  oz: 'ozon',
  ym: 'ym',
  yandex_market: 'ym',
  'yandex market': 'ym',
  yandexmarket: 'ym',
}

export function isMarketplaceCode(value: unknown): value is MarketplaceCode {
  return typeof value === 'string' && (MARKETPLACE_CODES as readonly string[]).includes(value)
}

/**
 * Приводит любое известное написание (`wildberries`, `WB`, `yandex_market`) к коду.
 * Незнакомое значение → `null`; решение, ошибка это или пропуск, — за вызывающим.
 */
export function normalizeMarketplace(value: unknown): MarketplaceCode | null {
  if (typeof value !== 'string') return null
  return ALIASES[value.trim().toLowerCase()] ?? null
}

/** То же, но незнакомое значение — ошибка с перечнем допустимых. */
export function requireMarketplace(value: unknown): MarketplaceCode {
  const code = normalizeMarketplace(value)
  if (!code) {
    throw new Error(
      `Неизвестная площадка «${String(value)}». Допустимо: ${MARKETPLACE_CODES.join(', ')}`,
    )
  }
  return code
}

export function marketplaceProfile(code: MarketplaceCode): MarketplaceProfile {
  return MARKETPLACES[code]
}

/** Площадка по значению `mp_sellers.marketplace`; `site` и прочие каналы → `null`. */
export function marketplaceBySellerValue(value: unknown): MarketplaceCode | null {
  return normalizeMarketplace(value)
}

export function marketplaceSupports(
  code: MarketplaceCode,
  capability: keyof MarketplaceProfile['capabilities'],
): boolean {
  return MARKETPLACES[code].capabilities[capability]
}

/** Для исчерпывающих `switch`: компилятор не пропустит новую площадку без ветки. */
export function assertNever(value: never, context = 'marketplace'): never {
  throw new Error(`Необработанное значение ${context}: ${String(value)}`)
}

// ── UI-поля без аналога на бэкенде ──────────────────────────────────────────

/**
 * Тон бейджа площадки в карточках/таблицах МП (обзор карточек макета, склейки
 * и т.п.). ВБ/ОЗОН — существующие цвета, взятые как есть из
 * `features/designs-v2/components/design-card/MarketplaceOverview.tsx` и
 * соседних экранов (их визуальное поведение правкой реестра не меняется).
 * Я.Маркет пока нигде не рисуется — тон подобран из готовой палитры
 * `components/ui/Badge` (жёлтый ближе всего к фирменному цвету площадки),
 * а не заведён новым.
 */
export const MARKETPLACE_BADGE_TONE: Readonly<Record<MarketplaceCode, BadgeVariant>> = {
  wb: 'purple',
  ozon: 'blue',
  ym: 'yellow',
}
